import { useContext, useEffect, useRef } from 'react'
import { UNSAFE_NavigationContext } from 'react-router-dom'

export default function useUnsavedChangesGuard({ when, onBlock }) {
  const { navigator } = useContext(UNSAFE_NavigationContext)
  const whenRef = useRef(when)
  const onBlockRef = useRef(onBlock)
  const allowLeaveRef = useRef(false)
  const ignoringRestorePopRef = useRef(false)

  whenRef.current = when
  onBlockRef.current = onBlock

  const allowLeave = () => {
    allowLeaveRef.current = true
  }

  useEffect(() => {
    const { push, replace, go } = navigator

    const intercept = (method) => (...args) => {
      if (allowLeaveRef.current || !whenRef.current) {
        allowLeaveRef.current = false
        method.apply(navigator, args)
        return
      }

      onBlockRef.current(() => {
        allowLeaveRef.current = true
        method.apply(navigator, args)
      })
    }

    navigator.push = intercept(push)
    navigator.replace = intercept(replace)
    navigator.go = intercept(go)

    return () => {
      navigator.push = push
      navigator.replace = replace
      navigator.go = go
    }
  }, [navigator])

  useEffect(() => {
    if (!when) return undefined

    const handleBeforeUnload = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [when])

  useEffect(() => {
    if (!when) return undefined

    const handlePopState = (event) => {
      if (ignoringRestorePopRef.current) {
        event.stopImmediatePropagation()
        ignoringRestorePopRef.current = false
        return
      }

      if (allowLeaveRef.current) {
        allowLeaveRef.current = false
        return
      }

      event.stopImmediatePropagation()
      ignoringRestorePopRef.current = true
      window.history.go(1)
      onBlockRef.current(() => {
        allowLeaveRef.current = true
        window.history.go(-1)
      })
    }

    window.addEventListener('popstate', handlePopState, true)
    return () => window.removeEventListener('popstate', handlePopState, true)
  }, [when])

  return { allowLeave }
}
